import React, {ChangeEvent, useEffect, useState} from 'react';
import {Calendar, type CalendarProps} from './calendar';
import {Popover, PopoverTrigger, PopoverContent} from './popover';
import {Button} from './button';
import {Input} from './input';
import {cn} from '@common-web/lib/utils';
import {CalendarIcon} from 'lucide-react';

export interface DatePickerProps {
  id?: string;
  value: string; // ISO string: yyyy-MM-dd or ''
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /**
   * Forwarded to the underlying `Calendar` (react-day-picker `DayPicker`).
   * Intentionally omits `mode`, `selected`, and `onSelect` — those are owned
   * by `DatePicker` (single-date selection backed by the controlled `value`)
   * and overriding them would put the picker into an inconsistent state.
   * Use this for caption layout, month bounds, disabled dates, etc.
   */
  calendarProps?: Omit<CalendarProps, 'mode' | 'selected' | 'onSelect'>;
}

export function DatePicker({
  id,
  value,
  onChange,
  placeholder = 'MM/DD/YYYY',
  disabled,
  className,
  calendarProps,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  // Create selectedDate without timezone issues
  const selectedDate = value ? new Date(value + 'T12:00:00') : undefined;

  // Update input value when external value changes
  useEffect(() => {
    if (value) {
      // Parse ISO date string directly to avoid timezone issues
      const [year, month, day] = value.split('-').map(Number);
      const monthStr = month.toString().padStart(2, '0');
      const dayStr = day.toString().padStart(2, '0');
      setInputValue(`${monthStr}/${dayStr}/${year}`);
    } else {
      setInputValue('');
    }
  }, [value]);

  const handleCalendarSelect = (date: Date | undefined) => {
    if (!date) {
      onChange('');
      setInputValue('');
    } else {
      // Extract date components to avoid timezone issues
      const year = date.getFullYear();
      const month = date.getMonth() + 1; // getMonth() returns 0-11
      const day = date.getDate();

      // Create ISO string directly
      const monthStr = month.toString().padStart(2, '0');
      const dayStr = day.toString().padStart(2, '0');
      const isoString = `${year}-${monthStr}-${dayStr}`;

      onChange(isoString);
      setInputValue(`${monthStr}/${dayStr}/${year}`);
    }
    setOpen(false);
  };

  const formatInput = (input: string) => {
    // Remove any non-numeric characters except /
    const cleaned = input.replace(/[^\d]/g, '');

    // Apply MM/DD/YYYY formatting
    if (cleaned.length <= 2) {
      return cleaned;
    } else if (cleaned.length <= 4) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
    } else {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4, 8)}`;
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const formatted = formatInput(rawValue);
    setInputValue(formatted);

    // Try to parse complete dates (MM/DD/YYYY format)
    if (formatted.length === 10) {
      try {
        // Parse the date components directly to avoid timezone issues
        const [month, day, year] = formatted.split('/').map(Number);

        // Validate the date components
        if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 1900) {
          // Create ISO string directly without timezone conversion
          const monthStr = month.toString().padStart(2, '0');
          const dayStr = day.toString().padStart(2, '0');
          const isoString = `${year}-${monthStr}-${dayStr}`;

          // Validate the date is actually valid (handles things like Feb 30)
          const testDate = new Date(isoString + 'T12:00:00');
          if (
            testDate.getFullYear() === year &&
            testDate.getMonth() === month - 1 &&
            testDate.getDate() === day
          ) {
            onChange(isoString);
          }
        }
      } catch {
        // Invalid date, don't update the value
      }
    } else if (formatted === '') {
      onChange('');
    }
  };

  const handleInputBlur = () => {
    // If input is empty, clear the value
    if (inputValue === '') {
      onChange('');
      return;
    }

    // If input is incomplete but has some content, revert to last valid date
    if (inputValue.length < 10) {
      if (value) {
        // Parse ISO date string directly to avoid timezone issues
        const [year, month, day] = value.split('-').map(Number);
        const monthStr = month.toString().padStart(2, '0');
        const dayStr = day.toString().padStart(2, '0');
        setInputValue(`${monthStr}/${dayStr}/${year}`);
      } else {
        setInputValue('');
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Allow navigation and deletion keys
    if (
      ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)
    ) {
      return;
    }

    // Only allow numbers
    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <div className={cn('relative', className)}>
      <Input
        id={id}
        type='text'
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className='pr-10 transition-colors'
        maxLength={10}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='absolute right-0 top-0 h-full px-3 hover:bg-accent/50 transition-colors rounded-l-none'
            disabled={disabled}
            aria-label='Open calendar'
          >
            <CalendarIcon className='h-4 w-4 text-muted-foreground hover:text-foreground transition-colors' />
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-auto p-0 shadow-lg border' align='start'>
          <Calendar
            mode='single'
            selected={selectedDate}
            onSelect={handleCalendarSelect}
            initialFocus
            {...calendarProps}
            className={cn('rounded-md', calendarProps?.className)}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
